// Formateo amigable de la bitácora y el comparador de versiones:
// - Etiquetas legibles de los nombres de campo (field labels).
// - Formateo de valores por campo (booleanos de validated -> Sí/No, fechas MX,
//   escalas -> lista legible vía scaleFormat).
//
// Se centraliza aquí para que la Bitácora (ServiceValidationDetail) y el
// comparador (ServiceValidationDiff) usen exactamente el mismo criterio.
import { fmtMxDateTime } from "../../helpers/datetime"
import { parseScales, scaleLine, isScalesField } from "./scaleFormat"

// Etiquetas de campo. Nota (según pedido del usuario): supplier_amount y
// supplier_amount_total comparten etiqueta "Monto total proveedor".
const FIELD_LABELS = {
  supplier_amount_total: "Monto total proveedor",
  supplier_amount: "Monto proveedor",
  supplier_amount_ori: "Monto proveedor original",
  customer_amount_ori: "Monto total cliente",
  customer_amount: "Monto cliente",
  scales: "Escalas",
  notes: "Notas",
  validated: "Validado",
  validated_by: "Validado por",
  validated_by_name: "Validado por (nombre)",
  onhold_by_name: "Tomado por (nombre)",
  route: "Ruta",
  on_hold: "On-hold",
}

// Prefijos de campo con etiqueta legible (p. ej. "pod:TICKET" -> "Evidencia (TICKET)").
const _podLabel = (field) => {
  const t = field.slice("pod:".length)
  return `Evidencia (${t})`
}

// Devuelve la etiqueta legible de un campo (o el nombre crudo si no hay mapeo).
export const fieldLabel = (field) => {
  if (typeof field === "string" && field.startsWith("pod:")) return _podLabel(field)
  return FIELD_LABELS[field] || field
}

// Campos cuyo valor es una fecha/hora (se muestran en formato MX).
const DATE_FIELDS = new Set(["validated_at"])

// ¿El valor representa "verdadero"? Tolera bool y strings de repr Python/JSON
// ("True"/"true"/"1") ya que la bitácora los guarda como str(value).
const isTruthyFlag = (v) => {
  if (typeof v === "boolean") return v
  const s = String(v).trim().toLowerCase()
  return s === "true" || s === "1"
}
const isFalsyFlag = (v) => {
  const s = String(v).trim().toLowerCase()
  return s === "false" || s === "0"
}

// ¿El campo es un flag booleano con render Sí/No? (validated y afines).
const BOOL_FIELDS = new Set([
  "validated",
  "close_without_customer",
  "manually_corrected",
  "pending_documentation",
])

// Campos cuyo valor es un identificador de usuario (sub). Se resuelven a nombre
// legible cuando se provee un `userResolver`.
const USER_FIELDS = new Set([
  "validated_by",
  "created_by",
  "changed_by",
  "onhold_by",
])

// Formatea el valor de un campo para bitácora/diff. Devuelve string o JSX.
// `t` es opcional (i18n); si no viene, usa Sí/No en español.
// `userResolver` (opcional): función (sub) -> nombre legible, para traducir los
// campos de usuario (validated_by/created_by/...) de sub a nombre.
export const formatFieldValue = (field, value, t, userResolver) => {
  const yes = (t && t("Yes")) || "Sí"
  const no = (t && t("No")) || "No"

  if (value === null || value === undefined || value === "") {
    // Para flags booleanos, ausencia se trata como No; para el resto, guion.
    return BOOL_FIELDS.has(field) ? no : "—"
  }

  // Campos de usuario: traducir sub -> nombre si hay resolvedor.
  if (USER_FIELDS.has(field) && typeof userResolver === "function") {
    return userResolver(String(value)) || String(value)
  }

  if (isScalesField(field)) {
    const scales = parseScales(value)
    if (scales === null) return String(value)
    if (scales.length === 0) return "Sin escalas"
    return (
      <ul className="mb-0 ps-3 small">
        {scales.map((sc, i) => (
          <li key={sc.external_id || i}>{scaleLine(sc, i)}</li>
        ))}
      </ul>
    )
  }

  if (BOOL_FIELDS.has(field)) {
    if (isTruthyFlag(value)) return yes
    if (isFalsyFlag(value)) return no
    return String(value)
  }

  if (DATE_FIELDS.has(field)) {
    return fmtMxDateTime(value)
  }

  // Campo 'route' (reconstrucción): el valor viene como "mode=B distance_km=40.33".
  // Se traduce a etiquetas legibles: mode -> "Modo", distance_km -> "Kilómetros".
  if (field === "route") {
    const raw = String(value)
    const parts = []
    const m = raw.match(/mode=([A-Za-z0-9]+)/)
    if (m) parts.push(`Modo ${m[1]}`)
    const d = raw.match(/distance_km=([0-9.]+)/)
    if (d) parts.push(`Kilómetros ${d[1]}`)
    return parts.length ? parts.join(" · ") : raw
  }

  return String(value)
}

// Etiquetas i18n de los componentes aritméticos del desglose de montos.
// Las keys conocidas se traducen; cualquier otra cae al label del backend o la
// propia key. Claves esperadas por el backend (PT-4069): price_km, price_time,
// proofs, cancellation.
const BREAKDOWN_COMPONENT_LABELS = {
  price_km: "PriceKm",
  price_time: "PriceTime",
  proofs: "Proofs",
  cancellation: "Cancellation",
}

// Etiqueta legible de un componente del desglose. Prefiere la clave i18n
// conocida; si no, usa el label que mande el backend; si tampoco, la key cruda.
const breakdownComponentLabel = (comp, t) => {
  const key = comp && comp.key
  const i18nKey = key && BREAKDOWN_COMPONENT_LABELS[key]
  if (i18nKey) {
    const translated = t && t(i18nKey)
    if (translated && translated !== i18nKey) return translated
  }
  return (comp && comp.label) || key || "—"
}

// Normaliza un lado del desglose (supplier/customer) a una estructura
// renderizable TOLERANTE: components siempre es array, total puede faltar.
const normalizeBreakdownSide = (side, t) => {
  if (!side || typeof side !== "object") return null
  const rawComponents = Array.isArray(side.components) ? side.components : []
  const components = rawComponents
    .filter((c) => c && typeof c === "object")
    .map((c) => ({
      key: c.key ?? null,
      label: breakdownComponentLabel(c, t),
      detail: c.detail ?? null,
      amount: c.amount ?? null,
    }))
  return {
    total: side.total ?? null,
    components,
  }
}

// formatAmountBreakdown(breakdown, t): helper PURO para la bitácora.
// Dado audit.amount_breakdown devuelve una estructura renderizable tolerante a
// shapes parciales (manual_edit / supplier / customer opcionales, components
// opcionales). Si breakdown es null/undefined/no-objeto devuelve null para que
// el consumidor NO renderice la sección (degrada sin romper la bitácora).
//
// Shape esperado del backend (PT-4069):
//   { manual_edit?: {field, old_value, new_value, changed_by_name, changed_at},
//     supplier?: {total, components:[{key,label,amount,detail?}]},
//     customer?: {total, components:[...]} }
// Componentes por key: price_km, price_time, proofs, cancellation.
export const formatAmountBreakdown = (breakdown, t) => {
  if (!breakdown || typeof breakdown !== "object") return null

  const me = breakdown.manual_edit
  const manualEdit = me && typeof me === "object"
    ? {
      field: me.field ?? null,
      old_value: me.old_value ?? null,
      new_value: me.new_value ?? null,
      changed_by_name: me.changed_by_name ?? null,
      changed_at: me.changed_at ?? null,
    }
    : null

  const supplier = normalizeBreakdownSide(breakdown.supplier, t)
  const customer = normalizeBreakdownSide(breakdown.customer, t)

  // Si no hay nada renderizable, devolver null (degrada sin romper).
  if (!manualEdit && !supplier && !customer) return null

  return { manualEdit, supplier, customer }
}
